<script setup lang="ts">
import {trackUser} from './utils/posthog';
import type {PlanObject} from './composables/usePlan';

const {$propelAuth} = useNuxtApp();
const colorMode = useColorMode();
if (colorMode?.preference) {
  colorMode.preference = 'light'; // Despite these docs (https://ui.nuxt.com/getting-started/theming#dark-mode), some customers were still getting dark mode if system was set to it
}

const route = useRoute();
if (route?.query?.redirect) {
  await navigateTo(route?.query?.redirect as string, {external: true});
}

useHead({
  meta: [{charset: 'utf-8'}, {name: 'viewport', content: 'width=device-width, initial-scale=1'}],
  link: [{rel: 'icon', href: '/img/favicon.png'}],
  htmlAttrs: {
    lang: 'en',
  },
  script: [
    {src: 'https://js.stripe.com/v3'},
    {
      children: `
        !function(e,r){try{if(e.vector)return void console.log("Vector snippet included more than once.");var t={};t.q=t.q||[];for(var o=["load","identify","on"],n=function(e){return function(){var r=Array.prototype.slice.call(arguments);t.q.push([e,r])}},c=0;c<o.length;c++){var a=o[c];t[a]=n(a)}if(e.vector=t,!t.loaded){var i=r.createElement("script");i.type="text/javascript",i.async=!0,i.src="https://cdn.vector.co/pixel.js";var l=r.getElementsByTagName("script")[0];l.parentNode.insertBefore(i,l),t.loaded=!0}}catch(e){console.error("Error loading Vector:",e)}}(window,document);
        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
          vector.load("3fd8b5b8-6820-4627-8943-f2b1f94c3a51");
        }
      `,
    },
    {
      children: `(function () {
        var e = window;
        var t = document;
        var n = function () {
          n.e(arguments);
        };
        n.q = [];
        n.e = function (e) {
          n.q.push(e);
        };
        e.Pylon = n;
        var r = function () {
          var e = t.createElement("script");
          e.setAttribute("type", "text/javascript");
          e.setAttribute("async", "true");
          e.setAttribute(
            "src",
            "https://widget.usepylon.com/widget/2e6349d2-09e8-49ee-8756-656cf20a7c8c"
          );
          var n = t.getElementsByTagName("script")[0];
          n.parentNode.insertBefore(e, n);
        };
        if (t.readyState === "complete") {
          r();
        } else if (e.addEventListener) {
          e.addEventListener("load", r, false);
        }
      })();`,
    },
  ],
});

const title = 'Vector';
const description = 'Person-level site identity';

useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
});

const auth = await ($propelAuth as any)?.getAuthenticationInfoOrNull();
const plan: PlanObject = await usePlan();

if (auth?.user) {
  trackUser(auth.user.userId, {
    email: auth.user.email,
    name: `${auth.user.firstName} ${auth.user.lastName}`,
    first_name: auth.user.firstName,
    last_name: auth.user.lastName,
    plan: plan.planName,
    is_annual_plan: plan.isAnnualPlan,
    has_paid_plan: plan.hasPaidPlan,
    user_created_at: auth.user.createdAt,
    login_method: auth.user.loginMethod,
  });

  (window as any).pylon = {
    chat_settings: {
      app_id: '2e6349d2-09e8-49ee-8756-656cf20a7c8c',
      email: auth.user.email,
      name: `${auth.user.firstName} ${auth.user.lastName}`,
    },
  };
}
</script>

<template>
  <NuxtLoadingIndicator />
  <ClientOnly>
    <UMain>
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
    </UMain>
    <UNotifications />
    <UModals />
    <USlideovers />
    <ConfirmationModal />
  </ClientOnly>
</template>

<style>
#vbackground {
  background: radial-gradient(circle at top, #3e6aee, #ffffff, #ffffff);
  background-size: 200% 100%;
  background-position: center -300px;
  background-repeat: no-repeat;
}

.PylonChat-bubbleFrameContainer {
  bottom: 32px !important;
}
.PylonChat-chatWindowFrameContainer {
  bottom: 32px !important;
}

/* UTable loading */
tr td.absolute {
  bottom: 0 !important;
  z-index: 1 !important;
}

progress {
  height: 3px !important;
  line-height: 3px !important;
}

progress.bar-animation-carousel::-webkit-progress-bar {
  background-color: transparent !important;
}

progress.bar-animation-carousel::-webkit-progress-bar.dark,
.dark progress.bar-animation-carousel::-webkit-progress-bar {
  background-color: transparent !important; /* or a lighter red if needed */
}

/* Firefox support */
progress.bar-animation-carousel {
  background-color: transparent !important;
}

.dark progress.bar-animation-carousel {
  background-color: transparent !important;
}
</style>
